/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';
import type { TrackingContextData } from 'js/lib/retracked';

import { Dialog } from '@coursera/cds-core';

const styles = {
  dialog: css`
    .cds-Dialog-dialog {
      min-height: fit-content;
    }
  `,
  children: css`
    padding: 0 10px 10px;
  `,
};

type Props = {
  handleClose?: () => void;
  modalName: string;
  allowClose?: boolean;
  trackingName?: string;
  data?: any;
  children: React.ReactNode;
};

type DiscussionsModalContextType = {
  _eventData: TrackingContextData;
};

const DiscussionsModal = (
  { allowClose = true, handleClose, modalName, trackingName, data, children }: React.PropsWithChildren<Props>,
  context: DiscussionsModalContextType
) => {
  const { _eventData } = context;

  // Updated this component to use cds Dialog instead of Modal, so replicating the tracking
  // done in 'bundles/ui/components/Modal'. Future update would require updating Modal to use cds Dialog
  useEffect(() => {
    if (trackingName) {
      Retracked.trackComponent(_eventData, data, trackingName, 'show');
    }
  }, [trackingName, data, _eventData]);

  const onClose = useCallback<() => void>(() => {
    if (allowClose && trackingName) {
      Retracked.trackComponent(_eventData, data, trackingName, 'close');
    }
    handleClose?.();
  }, [allowClose, trackingName, _eventData, data, handleClose]);

  return (
    <React.Fragment>
      <Dialog
        css={styles.dialog}
        open={true}
        onClose={allowClose ? onClose : undefined}
        aria-describedby={modalName}
        variant="standard"
      >
        <Dialog.Content css={styles.children}>{children}</Dialog.Content>
      </Dialog>
    </React.Fragment>
  );
};

DiscussionsModal.contextTypes = { _eventData: PropTypes.object };

export default DiscussionsModal;
